import { useEffect } from 'react';

import { ForcedSubject } from '@casl/ability';
import { useLocation, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { t } from '../translations/t';
import { selectTranslationsLoading } from '../translations/slice';

type Resources =
  | 'Employees'
  | 'Dashboard'
  | 'Absences'
  | 'Candidates'
  | 'Candidate'
  | 'Events'
  | 'EventTypes'
  | 'EventBatch'
  | 'PaperEvents'
  | 'User'
  | 'Policy'
  | 'Organization'
  | 'Competencies'
  | 'all'
  | 'Settings'
  | 'CompanyProfiles'
  | 'SettingsAdmin'
  | 'Attorneys'
  | 'Vacations'
  | 'VacationsSettings'
  | 'Payslips'
  | 'Personal'
  | 'SmevErrors'
  | 'Quiz'
  | 'GeneralNews'
  | 'News'
  | 'Review'
  | 'Categories'
  | 'SocialAccess'
  | 'SocialServices'
  | 'CompanyServices'
  | 'EmployeeServices'
  | ForcedSubject<
      | 'Employees'
      | 'Dashboard'
      | 'Absences'
      | 'Candidates'
      | 'Candidate'
      | 'Events'
      | 'EventTypes'
      | 'EventBatch'
      | 'PaperEvents'
      | 'User'
      | 'Policy'
      | 'Organization'
      | 'Competencies'
      | 'Settings'
      | 'CompanyProfiles'
      | 'SettingsAdmin'
      | 'Attorneys'
      | 'Vacations'
      | 'VacationsSettings'
      | 'Payslips'
      | 'Personal'
      | 'SmevErrors'
      | 'Quiz'
      | 'GeneralNews'
      | 'News'
      | 'Review'
      | 'Categories'
      | 'SocialAccess'
      | 'SocialServices'
      | 'CompanyServices'
      | 'EmployeeServices'
    >;

/** Установить заголовок страницы в браузере */
export const useMetaTitles = (
  metaTitles: {
    resource?: Resources;
    parentResource?: Resources;
    basename?: string;
    href: string;
    submenu?: {
      aqaClass: string;
      href: string;
      resource: Resources;
      parentResource?: Resources;
      basename: string;
    }[];
    completeTitle?: {
      company: string;
      employee: string;
    };
  }[],
  role: 'employee' | 'company',
) => {
  const { pathname } = useLocation();

  const translationLoadingStatus = useSelector(selectTranslationsLoading);

  const isTranslationsLoaded = translationLoadingStatus === 'complete';

  const sortedMetaTitles = [...metaTitles].sort(
    (title1, title2) => title1.href.length - title2.href.length,
  );

  const normalizePath = (path: string): string => {
    if (path.startsWith('/personal')) {
      return '/personal';
    }
    return path;
  };

  const normalizedPathname = normalizePath(pathname);

  const findMatchingTitle = () => {
    const exactMatch = sortedMetaTitles.find(
      (item) =>
        normalizedPathname === item.href ||
        normalizedPathname === `/company${item.href}` ||
        normalizedPathname === `/employee${item.href}`,
    );

    if (exactMatch) return exactMatch;

    return sortedMetaTitles.find((item) => {
      if (item.href === '/') {
        return normalizedPathname === item.href;
      } else {
        return (
          normalizedPathname.startsWith(item.href) ||
          normalizedPathname.startsWith(`/company${item.href}`) ||
          normalizedPathname.startsWith(`/employee${item.href}`)
        );
      }
    });
  };

  const {
    resource,
    parentResource,
    basename,
    submenu,
    href = 'not-found',
    completeTitle,
  } = findMatchingTitle() || {};

  const findedSubmenu =
    submenu &&
    submenu.find(
      (item) =>
        normalizedPathname === item.href ||
        normalizedPathname === `/company${item.href}` ||
        normalizedPathname === `/employee${item.href}`,
    );

  const submenuTitle =
    findedSubmenu &&
    t(
      `modules.${findedSubmenu.parentResource ?? findedSubmenu.resource}.${
        findedSubmenu.basename === 'sign-types'
          ? 'unep-types'
          : findedSubmenu.basename
      }.${role}Title`,
    );

  const titleFromResource =
    submenuTitle ||
    ((resource && basename) || resource === 'Settings'
      ? t(`modules.${parentResource ?? resource}.${basename}.${role}Title`)
      : '');

  const titleWithSubmenu = isTranslationsLoaded ? titleFromResource : '';

  const title = completeTitle ? completeTitle[role] : titleWithSubmenu || '';

  const matchGeneral = useMatch(`${href}/*`);
  const employeeMatch = useMatch(`/employee${href}/*`);
  const companyMatch = useMatch(`/company${href}/*`);
  const match = matchGeneral || employeeMatch || companyMatch;

  useEffect(() => {
    if (!match) {
      document.title = `VK HR Tek - Страница не найдена`;
    } else {
      document.title = `VK HR Tek${title ? ` - ${title}` : ''}`;
    }
  }, [pathname, title, href, match, role, metaTitles]);
};
